<template>
    <div class="mt-4">
        <div class="d-flex justify-content-between align-items-center chart-header mb-2">
            <div>
                <h2 class="mb-2 mb-lg-0 h2-small">
                    <Translate translationKey="charts.chartTitle"
                               :replacements="{name: cryptoCurrency.name, currency_code1: cryptoCurrency.currency_code, currency_code2: fiatCurrency}" />
                </h2>
            </div>
            <div v-show="selectedTabIndex < 3"
                 class="d-flex justify-content-center align-items-center">
                <div v-if="selectedTabIndex < 3"
                     id="chart-expand"
                     class="d-flex justify-content-center align-items-center chart-expand"
                     @click="toggleFs">
                    <i class="fa-solid fa-expand" />
                </div>
                <div v-for="(type, index) in chartType"
                     v-show="selectedTabIndex === index && type !== 'trading-view' && !hideExport"
                     :id="`export-menu-tippy-${type}-export`"
                     :key="type"
                     class="export-menu"
                     role="tooltip"
                     aria-label="Export menu">
                    <i class="fa-solid fa-share" />
                </div>
            </div>
            <div v-if="selectedTabIndex >= 3"
                 class="empty-space" />
        </div>
        <div class="d-flex selectors justify-content-between align-items-center flex-wrap chart-types-container">
            <div class="d-flex flex-row align-items-center view-selector"
                 :class="`tabs-count-${Object.keys(mergedTabTitles).length}`">
                <button v-for="(tab, index) in Object.keys(mergedTabTitles)"
                        :key="tab"
                        class="d-flex btn"
                        :class="{active: (index === selectedTabIndex)}"
                        @click="selectTab(tab, index)">
                    <span v-if="tab === 'trading-view'">
                        {{ mergedTabTitles[tab] }}
                    </span>
                    <span v-else>
                        <Translate :translationKey="'cryptocurrencies.' + mergedTabTitles[tab]" />
                    </span>
                </button>
            </div>
            <div v-if="selectedTabIndex < 3"
                 class="range-selector d-flex flex-row align-items-center">
                <button v-for="(item, index) in Object.keys(rangeSelector)"
                        :key="index"
                        class="btn"
                        :style="'display: ' + (cryptoCurrency.status !== 'active' && index < 1 ? 'none' : 'flex')"
                        :class="{active: (index === selectedRangeIndex)}"
                        @click="handleTimeframeClick(item, index)">
                    <span class="text-uppercase">
                        {{ rangeSelector[item] }}
                    </span>
                </button>
                <button class="position-relative d-flex btn"
                        :class="{active: isLogarithmic}"
                        @click="toggleLogAxis">
                    <span class="log-view text-uppercase">
                        <Translate translationKey="time.log"
                                   fallback="LOG" />
                    </span>
                </button>
            </div>
        </div>
        <slot />
    </div>
</template>

<script>
    import { getTrans } from '../helpers/getTrans';

    export default {
        name: 'Tabs',
        provide() {
            return {
                currency_code: this.cryptoCurrency.currency_code,
            };
        },
        props: {
            cryptoCurrency: {
                type: Object,
                required: true,
                default: () => ({}),
            },
            tabTitles: {
                type: Array,
                required: true,
                default: () => ['Price', 'Market Cap', 'Candlestick'],
            },
            fiatCurrency: {
                type: String,
                required: true,
            },
            chartType: {
                type: Array,
                default: () => ['price', 'market-cap', 'candlestick'],
                required: false,
            },
            defaultTabIndex: {
                type: Number,
                default: 0,
                required: false,
            },
            defaultTabTitle: {
                type: String,
                default: 'price',
                required: false,
            },
            defaultRangeIndex: {
                type: Number,
                default: 0,
                required: false,
            },
        },
        data() {
            return {
                selectedTabIndex: null,
                selectedTabTitle: null,
                selectedRangeIndex: null,
                hideExport: false,
                rangeSelector: {
                    '1d': getTrans('time.oneDay', '1d'),
                    '1w': getTrans('time.oneWeek', '1w'),
                    '1m': getTrans('time.oneMonth', '1m'),
                    '3m': getTrans('time.threeMonths', '3m'),
                    '6m': getTrans('time.sixMonths', '6m'),
                    '1y': getTrans('time.oneYear', '1y'),
                    ytd: getTrans('time.ytd', 'ytd'),
                    all: getTrans('charts.all'),
                },
                isLogarithmic: false,
                mergedTabTitles: {},
            };
        },
        mounted() {
            this.mergedTabTitles = this.tabTitles.reduce((accumulator, current) => ({ ...accumulator, ...current }), {});
            this.selectTab(this.defaultTabTitle, this.defaultTabIndex, false);
            this.selectRange(this.cryptoCurrency.status === 'active' ? this.defaultRangeIndex : 1);
        },
        methods: {
            selectTab(title, i, changeRange = true) {
                this.selectedTabTitle = title;
                if (this.selectedTabIndex !== i) {
                    this.selectedTabIndex = i;
                    if (this.selectedTabIndex < 3) {
                        this.selectRange(this.selectedRangeIndex, changeRange);
                    }
                }
                if (this.selectedTabIndex === 3) {
                    this.$emit('loadChart', { title: this.selectedTabTitle });
                }
            },
            selectRange(i, changeTabs = false) {
                if (this.selectedRangeIndex !== i || changeTabs) {
                    this.selectedRangeIndex = i;
                    const data = {
                        title: this.selectedTabTitle,
                        slug: this.cryptoCurrency.slug,
                        range: Object.keys(this.rangeSelector)[this.selectedRangeIndex],
                    };
                    this.$emit('loadChart', data);
                }

                this.hideExport = this.selectedRangeIndex === 7;
            },
            handleTimeframeClick(item, index) {
                if (!(this.cryptoCurrency.status !== 'active' && index < 1)) {
                    this.selectRange(index);
                }

                this.$emit('timeframe-change', item);
            },
            toggleLogAxis() {
                this.$emit('setLogarithmicAxis', { title: this.selectedTabTitle, isLogarithmic: this.isLogarithmic });
                this.isLogarithmic = !this.isLogarithmic;
            },
            toggleFs() {
                this.$emit('toggleFs', this.selectedTabTitle);
            },
        },
    };
</script>

<style scoped lang="scss">
.empty-space {
    height: 40px;
}

@media (max-width: 990px) {
    .empty-space {
        height: 30px;
    }
}
</style>
